import { getDatabase, ref, set } from 'firebase/database';
import { menuCategories } from '../data/menuData';
import { Category, MenuItemWithTranslations, AddOn } from '../types/menu';
import type { Language, TranslationStructure } from '../translations/types';

// First, define the supplements that can be added to items
const defaultSupplements: AddOn[] = [
  { id: 'fromage', name: 'Fromage', price: 5 },
  { id: 'viandeHachee', name: 'Viande Hachée', price: 5 },
  { id: 'champignon', name: 'Champignon', price: 5 },
  { id: 'poulet', name: 'Poulet', price: 5 },
  { id: 'thon', name: 'Thon', price: 5 },
  { id: 'pepperoni', name: 'Pepperoni', price: 5 },
  { id: 'mer', name: 'Fruits de Mer', price: 5 },
  { id: 'saucisse', name: 'Saucisse', price: 5 },
  { id: 'sauceAlgerienne', name: 'Sauce Algérienne', price: 2 },
  { id: 'sauceAndalouse', name: 'Sauce Andalouse', price: 2 },
  { id: 'sauceSamourai', name: 'Sauce Samouraï', price: 2 },
  { id: 'sauceCurry', name: 'Sauce Curry', price: 2 }
];

const defaultMenuItems: MenuItemWithTranslations[] = [
  // BURRITOS
  {
    id: 'burritoSaucisse',
    price: 23,
    category: 'burritos',
    icon: "🌯",
    image: "🌯", // Placeholder for missing image
    order: 0,
    translations: {
      fr: { name: 'Burrito Saucisse', description: 'Burrito avec saucisse' },
      ar: { name: 'بوريتو بالنقانق', description: 'بوريتو محشو بالنقانق' },
      en: { name: 'Sausage Burrito', description: 'Burrito with sausage' }
    }
  },
  {
    id: 'burritoDinde',
    price: 25,
    category: 'burritos',
    icon: "🌯",
    image: "🌯",
    order: 1,
    translations: {
      fr: { name: 'Burrito Dinde', description: 'Burrito à la dinde' },
      ar: { name: 'بوريتو بالديك الرومي', description: 'بوريتو بالديك الرومي' },
      en: { name: 'Turkey Burrito', description: 'Turkey burrito' }
    }
  },
  {
    id: 'burritoNuggets',
    price: 25,
    category: 'burritos',
    icon: "🌯",
    image: "🌯",
    order: 2,
    translations: {
      fr: { name: 'Burrito Nuggets', description: 'Burrito aux nuggets' },
      ar: { name: 'بوريتو ناجتس', description: 'بوريتو بقطع الدجاج المقرمشة' },
      en: { name: 'Nuggets Burrito', description: 'Chicken nuggets burrito' }
    }
  },
  {
    id: 'burritoEscalopes',
    price: 25,
    category: 'burritos',
    icon: "🌯",
    image: "🌯",
    order: 3,
    translations: {
      fr: { name: 'Burrito Escalopes', description: 'Burrito aux escalopes' },
      ar: { name: 'بوريتو إسكالوب', description: 'بوريتو بشرائح الدجاج' },
      en: { name: 'Escalope Burrito', description: 'Chicken escalope burrito' }
    }
  },
  {
    id: 'burritoViandeHachee',
    price: 25,
    category: 'burritos',
    icon: "🌯",
    image: "🌯",
    order: 4,
    translations: {
      fr: { name: 'Burrito Viande Hachée', description: 'Burrito à la viande hachée' },
      ar: { name: 'بوريتو باللحم المفروم', description: 'بوريتو باللحم المفروم' },
      en: { name: 'Ground Beef Burrito', description: 'Ground beef burrito' }
    }
  },
  {
    id: 'burritoCordonBlue',
    price: 30,
    category: 'burritos',
    icon: "🌯",
    image: "🌯",
    order: 5,
    translations: {
      fr: { name: 'Burrito Cordon Blue', description: 'Burrito cordon bleu' },
      ar: { name: 'بوريتو كوردون بلو', description: 'بوريتو كوردون بلو' },
      en: { name: 'Cordon Bleu Burrito', description: 'Cordon bleu burrito' }
    }
  },
  {
    id: 'burritoMixte',
    price: 30,
    category: 'burritos',
    icon: "🌯",
    image: "🌯",
    order: 6,
    translations: {
      fr: { name: 'Burrito Mixte', description: 'Burrito mixte avec plusieurs garnitures' },
      ar: { name: 'بوريتو مشكل', description: 'بوريتو مشكل بحشوات متنوعة' },
      en: { name: 'Mixed Burrito', description: 'Mixed burrito with various fillings' }
    }
  },

  // TACOS
  {
    id: 'tacosSaucisse',
    price: 33,
    category: 'tacos',
    icon: "🌮",
    image: "🌮",
    order: 0,
    translations: {
      fr: { name: 'Tacos Saucisse', description: 'Tacos à la saucisse' },
      ar: { name: 'تاكوس بالنقانق', description: 'تاكوس بالنقانق' },
      en: { name: 'Sausage Tacos', description: 'Sausage tacos' }
    }
  },
  {
    id: 'tacosDinde',
    price: 35,
    category: 'tacos',
    icon: "🌮",
    image: "🌮",
    order: 1,
    translations: {
      fr: { name: 'Tacos Dinde', description: 'Tacos à la dinde' },
      ar: { name: 'تاكوس بالديك الرومي', description: 'تاكوس بالديك الرومي' },
      en: { name: 'Turkey Tacos', description: 'Turkey tacos' }
    }
  },
  {
    id: 'tacosNuggets',
    price: 35,
    category: 'tacos',
    icon: "🌮",
    image: "🌮",
    order: 2,
    translations: {
      fr: { name: 'Tacos Nuggets', description: 'Tacos aux nuggets' },
      ar: { name: 'تاكوس ناجتس', description: 'تاكوس بقطع الدجاج المقرمشة' },
      en: { name: 'Nuggets Tacos', description: 'Chicken nuggets tacos' }
    }
  },
  {
    id: 'tacosEscalopes',
    price: 35,
    category: 'tacos',
    icon: "🌮",
    image: "🌮",
    order: 3,
    translations: {
      fr: { name: 'Tacos Escalopes', description: 'Tacos aux escalopes' },
      ar: { name: 'تاكوس إسكالوب', description: 'تاكوس بشرائح الدجاج' },
      en: { name: 'Escalope Tacos', description: 'Chicken escalope tacos' }
    }
  },
  {
    id: 'tacosViandeHachee',
    price: 35,
    category: 'tacos',
    icon: "🌮",
    image: "🌮",
    order: 4,
    translations: {
      fr: { name: 'Tacos Viande Hachée', description: 'Tacos à la viande hachée' },
      ar: { name: 'تاكوس باللحم المفروم', description: 'تاكوس باللحم المفروم' },
      en: { name: 'Ground Beef Tacos', description: 'Ground beef tacos' }
    }
  },
  {
    id: 'tacosCordonBlue',
    price: 37,
    category: 'tacos',
    icon: "🌮",
    image: "🌮",
    order: 5,
    translations: {
      fr: { name: 'Tacos Cordon Blue', description: 'Tacos cordon bleu' },
      ar: { name: 'تاكوس كوردون بلو', description: 'تاكوس كوردون بلو' },
      en: { name: 'Cordon Bleu Tacos', description: 'Cordon bleu tacos' }
    }
  },
  {
    id: 'tacosMixte',
    price: 40,
    category: 'tacos',
    icon: "🌮",
    image: "🌮",
    order: 6,
    translations: {
      fr: { name: 'Tacos Mixte', description: 'Tacos mixte avec plusieurs garnitures' },
      ar: { name: 'تاكوس مشكل', description: 'تاكوس مشكل بحشوات متنوعة' },
      en: { name: 'Mixed Tacos', description: 'Mixed tacos with various fillings' }
    }
  },

  // PIZZAS - LES INCONTOURNABLES
  {
    id: 'pizzaMargarita',
    price: 25,
    category: 'pizzas',
    subcategory: 'incontournables',
    icon: "🍕",
    image: "🍕",
    order: 0,
    translations: {
      fr: {
        name: 'Pizza Margarita',
        description: 'Sauce tomate, Mozzarella, Olives, Poivrons'
      },
      ar: {
        name: 'بيتزا مارغريتا',
        description: 'صلصة طماطم، موزاريلا، زيتون، فلفل'
      },
      en: {
        name: 'Margherita Pizza',
        description: 'Tomato sauce, Mozzarella, Olives, Peppers'
      }
    },
    addOns: defaultSupplements
  },
  {
    id: 'pizzaBolognaise',
    price: 35,
    category: 'pizzas',
    subcategory: 'incontournables',
    icon: "🍕",
    image: "🍕",
    order: 1,
    translations: {
      fr: {
        name: 'Pizza Bolognaise',
        description: 'Sauce tomate, Mozzarella, Viande hachée, Olives, Poivrons'
      },
      ar: {
        name: 'بيتزا بولونيز',
        description: 'صلصة طماطم، موزاريلا، لحم مفروم، زيتون، فلفل'
      },
      en: {
        name: 'Bolognese Pizza',
        description: 'Tomato sauce, Mozzarella, Ground meat, Olives, Peppers'
      }
    },
    addOns: defaultSupplements
  },
  {
    id: 'pizza4Fromages',
    price: 30,
    category: 'pizzas',
    subcategory: 'incontournables',
    icon: "🍕",
    image: "🍕",
    order: 2,
    translations: {
      fr: {
        name: 'Pizza 4 Fromages',
        description: 'Sauce tomate ou sauce blanche, Mozzarella, Fromage Edam, Roquefort, Fromage frais'
      },
      ar: {
        name: 'بيتزا 4 أجبان',
        description: 'صلصة طماطم أو بيضاء، موزاريلا، جبنة إيدام، روكفور، جبنة طازجة'
      },
      en: {
        name: '4 Cheese Pizza',
        description: 'Tomato or white sauce, Mozzarella, Edam cheese, Roquefort, Fresh cheese'
      }
    },
    addOns: defaultSupplements
  },
  {
    id: 'pizzaThon',
    price: 30,
    category: 'pizzas',
    subcategory: 'incontournables',
    icon: "🍕",
    image: "🍕",
    order: 3,
    translations: {
      fr: {
        name: 'Pizza Thon',
        description: 'Sauce tomate, Mozzarella, Thon, Olives, Poivrons, Oignons'
      },
      ar: {
        name: 'بيتزا تونة',
        description: 'صلصة طماطم، موزاريلا، تونة، زيتون، فلفل، بصل'
      },
      en: {
        name: 'Tuna Pizza',
        description: 'Tomato sauce, Mozzarella, Tuna, Olives, Peppers, Onions'
      }
    },
    addOns: defaultSupplements
  },
  {
    id: 'pizzaVegetarienne',
    price: 30,
    category: 'pizzas',
    subcategory: 'incontournables',
    icon: "🍕",
    image: "🍕",
    order: 4,
    translations: {
      fr: {
        name: 'Pizza Végétarienne',
        description: 'Sauce tomate, Mozzarella, Aubergines, Courgettes, Poivrons, Olives'
      },
      ar: {
        name: 'بيتزا نباتية',
        description: 'صلصة طماطم، موزاريلا، باذنجان، كوسة، فلفل، زيتون'
      },
      en: {
        name: 'Vegetarian Pizza',
        description: 'Tomato sauce, Mozzarella, Eggplant, Zucchini, Peppers, Olives'
      }
    },
    addOns: defaultSupplements
  },

  // PANINIS
  {
    id: 'paniniThon',
    price: 15,
    category: 'paninis',
    icon: "🥖",
    image: "🥖",
    order: 0,
    translations: {
      fr: { name: 'Panini Thon', description: 'Panini au thon' },
      ar: { name: 'بانيني تونة', description: 'بانيني بالتونة' },
      en: { name: 'Tuna Panini', description: 'Tuna panini' }
    }
  },
  {
    id: 'paniniSaucisse',
    price: 15,
    category: 'paninis',
    icon: "🥖",
    image: "🥖",
    order: 1,
    translations: {
      fr: { name: 'Panini Saucisse', description: 'Panini à la saucisse' },
      ar: { name: 'بانيني بالنقانق', description: 'بانيني بالنقانق' },
      en: { name: 'Sausage Panini', description: 'Sausage panini' }
    }
  },
  {
    id: 'paniniDinde',
    price: 22,
    category: 'paninis',
    icon: "🥖",
    image: "🥖",
    order: 2,
    translations: {
      fr: { name: 'Panini Dinde', description: 'Panini à la dinde' },
      ar: { name: 'بانيني بالديك الرومي', description: 'بانيني بالديك الرومي' },
      en: { name: 'Turkey Panini', description: 'Turkey panini' }
    }
  },

  // PIZZAS - LES FRUITS DE MER
  {
    id: 'pizzaCalamar',
    price: 35,
    category: 'pizzas',
    subcategory: 'fruitsmer',
    icon: "🍕",
    image: "🍕",
    order: 0,
    translations: {
      fr: {
        name: 'Pizza Calamar',
        description: 'Sauce tomate, Mozzarella, Calamar, Olives, Poivrons'
      },
      ar: {
        name: 'بيتزا كلمار',
        description: 'صلصة طماطم، موزاريلا، كلمار، زيتون، فلفل'
      },
      en: {
        name: 'Calamari Pizza',
        description: 'Tomato sauce, Mozzarella, Calamari, Olives, Peppers'
      }
    },
    addOns: defaultSupplements
  },
  {
    id: 'pizzaGambli',
    price: 35,
    category: 'pizzas',
    subcategory: 'fruitsmer',
    icon: "🍕",
    image: "🍕",
    order: 1,
    translations: {
      fr: {
        name: 'Pizza Gambli',
        description: 'Sauce tomate, Mozzarella, Crevettes, Olives, Poivrons'
      },
      ar: {
        name: 'بيتزا جمبري',
        description: 'صلصة طماطم، موزاريلا، جمبري، زيتون، فلفل'
      },
      en: {
        name: 'Shrimp Pizza',
        description: 'Tomato sauce, Mozzarella, Shrimp, Olives, Peppers'
      }
    },
    addOns: defaultSupplements
  },
  {
    id: 'pizzaFruitsDeMer',
    price: 50,
    category: 'pizzas',
    subcategory: 'fruitsmer',
    icon: "🍕",
    image: "🍕",
    order: 2,
    translations: {
      fr: {
        name: 'Pizza Fruits de Mer',
        description: 'Sauce tomate, Mozzarella, Crevettes, Calamar, Surimi, Olives, Poivrons'
      },
      ar: {
        name: 'بيتزا فواكه البحر',
        description: 'صلصة طماطم، موزاريلا، جمبري، كلمار، سوريمي، زيتون، فلفل'
      },
      en: {
        name: 'Seafood Pizza',
        description: 'Tomato sauce, Mozzarella, Shrimp, Calamari, Surimi, Olives, Peppers'
      }
    },
    addOns: defaultSupplements
  },

  // PIZZAS - LES MIXTES
  {
    id: 'pizzaMixte',
    price: 40,
    category: 'pizzas',
    subcategory: 'mixtes',
    icon: "🍕",
    image: "🍕",
    order: 0,
    translations: {
      fr: {
        name: 'Pizza Mixte',
        description: 'Sauce tomate, Mozzarella, Viande hachée, Poulet, Olives, Poivrons'
      },
      ar: {
        name: 'بيتزا مشكلة',
        description: 'صلصة طماطم، موزاريلا، لحم مفروم، دجاج، زيتون، فلفل'
      },
      en: {
        name: 'Mixed Pizza',
        description: 'Tomato sauce, Mozzarella, Ground meat, Chicken, Olives, Peppers'
      }
    },
    addOns: defaultSupplements
  },
  {
    id: 'pizzaRoyale',
    price: 50,
    category: 'pizzas',
    subcategory: 'mixtes',
    icon: "🍕",
    image: "🍕",
    order: 1,
    translations: {
      fr: {
        name: 'Pizza Royale',
        description: 'Sauce tomate, Mozzarella, Champignons, Poulet, Fruits de mer, Viande hachée, Olives, Poivrons'
      },
      ar: {
        name: 'بيتزا رويال',
        description: 'صلصة طماطم، موزاريلا، فطر، دجاج، فواكه البحر، لحم مفروم، زيتون، فلفل'
      },
      en: {
        name: 'Royal Pizza',
        description: 'Tomato sauce, Mozzarella, Mushrooms, Chicken, Seafood, Ground meat, Olives, Peppers'
      }
    },
    addOns: defaultSupplements
  },

  // PANINIS (remaining items)
  {
    id: 'paniniNuggets',
    price: 22,
    category: 'paninis',
    icon: "🥖",
    image: "🥖",
    order: 3,
    translations: {
      fr: { name: 'Panini Nuggets', description: 'Panini aux nuggets' },
      ar: { name: 'بانيني ناجتس', description: 'بانيني بقطع الدجاج المقرمشة' },
      en: { name: 'Nuggets Panini', description: 'Chicken nuggets panini' }
    }
  },
  {
    id: 'paniniEscalopes',
    price: 22,
    category: 'paninis',
    icon: "🥖",
    image: "🥖",
    order: 4,
    translations: {
      fr: { name: 'Panini Escalopes', description: 'Panini aux escalopes' },
      ar: { name: 'بانيني إسكالوب', description: 'بانيني بشرائح الدجاج' },
      en: { name: 'Escalope Panini', description: 'Chicken escalope panini' }
    }
  },

  // SANDWICHES
  {
    id: 'sandwichSaucisse',
    price: 15,
    category: 'sandwiches',
    icon: "🥪",
    image: "🥪",
    order: 0,
    translations: {
      fr: { name: 'Sandwich Saucisse', description: 'Sandwich à la saucisse' },
      ar: { name: 'ساندويتش نقانق', description: 'ساندويتش بالنقانق' },
      en: { name: 'Sausage Sandwich', description: 'Sausage sandwich' }
    }
  },
  {
    id: 'sandwichThon',
    price: 15,
    category: 'sandwiches',
    icon: "🥪",
    image: "🥪",
    order: 1,
    translations: {
      fr: { name: 'Sandwich Thon', description: 'Sandwich au thon' },
      ar: { name: 'ساندويتش تونة', description: 'ساندويتش بالتونة' },
      en: { name: 'Tuna Sandwich', description: 'Tuna sandwich' }
    }
  },
  {
    id: 'sandwichNuggets',
    price: 20,
    category: 'sandwiches',
    icon: "🥪",
    image: "🥪",
    order: 2,
    translations: {
      fr: { name: 'Sandwich Nuggets', description: 'Sandwich aux nuggets' },
      ar: { name: 'ساندويتش ناجتس', description: 'ساندويتش بقطع الدجاج المقرمشة' },
      en: { name: 'Nuggets Sandwich', description: 'Chicken nuggets sandwich' }
    }
  },
  {
    id: 'sandwichDinde',
    price: 20,
    category: 'sandwiches',
    icon: "🥪",
    image: "🥪",
    order: 3,
    translations: {
      fr: { name: 'Sandwich Dinde', description: 'Sandwich à la dinde' },
      ar: { name: 'ساندويتش ديك رومي', description: 'ساندويتش بالديك الرومي' },
      en: { name: 'Turkey Sandwich', description: 'Turkey sandwich' }
    }
  },
  {
    id: 'sandwichViandeHachee',
    price: 20,
    category: 'sandwiches',
    icon: "🥪",
    image: "🥪",
    order: 4,
    translations: {
      fr: { name: 'Sandwich Viande Hachée', description: 'Sandwich à la viande hachée' },
      ar: { name: 'ساندويتش لحم مفروم', description: 'ساندويتش باللحم المفروم' },
      en: { name: 'Ground Beef Sandwich', description: 'Ground beef sandwich' }
    }
  },
  {
    id: 'sandwichEscalopes',
    price: 20,
    category: 'sandwiches',
    icon: "🥪",
    image: "🥪",
    order: 5,
    translations: {
      fr: { name: 'Sandwich Escalopes', description: 'Sandwich aux escalopes' },
      ar: { name: 'ساندويتش إسكالوب', description: 'ساندويتش بشرائح الدجاج' },
      en: { name: 'Escalope Sandwich', description: 'Chicken escalope sandwich' }
    }
  },
  {
    id: 'sandwichCordonBlue',
    price: 25,
    category: 'sandwiches',
    icon: "🥪",
    image: "🥪",
    order: 6,
    translations: {
      fr: { name: 'Sandwich Cordon Blue', description: 'Sandwich cordon bleu' },
      ar: { name: 'ساندويتش كوردون بلو', description: 'ساندويتش كوردون بلو' },
      en: { name: 'Cordon Bleu Sandwich', description: 'Cordon bleu sandwich' }
    }
  },
  {
    id: 'sandwichMixte',
    price: 25,
    category: 'sandwiches',
    icon: "🥪",
    image: "🥪",
    order: 7,
    translations: {
      fr: { name: 'Sandwich Mixte', description: 'Sandwich mixte avec plusieurs garnitures' },
      ar: { name: 'ساندويتش مشكل', description: 'ساندويتش مشكل بحشوات متنوعة' },
      en: { name: 'Mixed Sandwich', description: 'Mixed sandwich with various fillings' }
    }
  },

  // PLATS
  {
    id: 'platsSaucisse',
    price: 35,
    category: 'plats',
    icon: "🍽️",
    image: "🍽️",
    order: 0,
    translations: {
      fr: { name: 'Plat Saucisse', description: 'Plat avec saucisse' },
      ar: { name: 'طبق نقانق', description: 'طبق بالنقانق' },
      en: { name: 'Sausage Plate', description: 'Plate with sausage' }
    }
  },
  {
    id: 'platsNuggets',
    price: 35,
    category: 'plats',
    icon: "🍽️",
    image: "🍽️",
    order: 1,
    translations: {
      fr: { name: 'Plat Nuggets', description: 'Plat de nuggets' },
      ar: { name: 'طبق ناجتس', description: 'طبق بقطع الدجاج المقرمشة' },
      en: { name: 'Nuggets Plate', description: 'Plate with chicken nuggets' }
    }
  },
  {
    id: 'platsDinde',
    price: 35,
    category: 'plats',
    icon: "🍽️",
    image: "🍽️",
    order: 2,
    translations: {
      fr: { name: 'Plat Dinde', description: 'Plat de dinde' },
      ar: { name: 'طبق ديك رومي', description: 'طبق بالديك الرومي' },
      en: { name: 'Turkey Plate', description: 'Plate with turkey' }
    }
  },
  {
    id: 'platsViandeHachee',
    price: 35,
    category: 'plats',
    icon: "🍽️",
    image: "🍽️",
    order: 3,
    translations: {
      fr: { name: 'Plat Viande Hachée', description: 'Plat de viande hachée' },
      ar: { name: 'طبق لحم مفروم', description: 'طبق باللحم المفروم' },
      en: { name: 'Ground Beef Plate', description: 'Plate with ground beef' }
    }
  },
  {
    id: 'platsEscalopes',
    price: 35,
    category: 'plats',
    icon: "🍽️",
    image: "🍽️",
    order: 4,
    translations: {
      fr: { name: 'Plat Escalopes', description: 'Plat d\'escalopes' },
      ar: { name: 'طبق إسكالوب', description: 'طبق بشرائح الدجاج' },
      en: { name: 'Escalope Plate', description: 'Plate with chicken escalope' }
    }
  },
  {
    id: 'platsCordonBlue',
    price: 35,
    category: 'plats',
    icon: "🍽️",
    image: "🍽️",
    order: 5,
    translations: {
      fr: { name: 'Plat Cordon Blue', description: 'Plat cordon bleu' },
      ar: { name: 'طبق كوردون بلو', description: 'طبق كوردون بلو' },
      en: { name: 'Cordon Bleu Plate', description: 'Plate with cordon bleu' }
    }
  },
  {
    id: 'platsMixte',
    price: 40,
    category: 'plats',
    icon: "🍽️",
    image: "🍽️",
    order: 6,
    translations: {
      fr: { name: 'Plat Mixte', description: 'Plat mixte avec plusieurs garnitures' },
      ar: { name: 'طبق مشكل', description: 'طبق مشكل بأصناف متنوعة' },
      en: { name: 'Mixed Plate', description: 'Mixed plate with various items' }
    }
  },

  // SALADES
  {
    id: 'saladeNicoise',
    price: 15,
    category: 'salades',
    icon: "🥗",
    image: "🥗",
    order: 0,
    translations: {
      fr: { name: 'Salade Niçoise', description: 'Salade niçoise traditionnelle' },
      ar: { name: 'سلطة نيسواز', description: 'سلطة نيسواز تقليدية' },
      en: { name: 'Niçoise Salad', description: 'Traditional Niçoise salad' }
    }
  },
  {
    id: 'saladeDeChef',
    price: 20,
    category: 'salades',
    icon: "🥗",
    image: "🥗",
    order: 1,
    translations: {
      fr: { name: 'Salade De Chef', description: 'Salade spéciale du chef' },
      ar: { name: 'سلطة الشيف', description: 'سلطة الشيف الخاصة' },
      en: { name: 'Chef\'s Salad', description: 'Special chef\'s salad' }
    }
  },
  {
    id: 'saladeRoyal',
    price: 25,
    category: 'salades',
    icon: "🥗",
    image: "🥗",
    order: 2,
    translations: {
      fr: { name: 'Salade Royal', description: 'Salade royale garnie' },
      ar: { name: 'سلطة رويال', description: 'سلطة رويال مميزة' },
      en: { name: 'Royal Salad', description: 'Garnished royal salad' }
    }
  }
];

// Define menuTranslations.
// SINGLE SOURCE OF TRUTH for all UI translations: injected into i18next as
// `resources` in src/config/i18n.ts at init (synchronously, no HTTP request).
// The `Record<Language, TranslationStructure>` annotation makes the compiler
// verify that every translation key exists in fr, ar AND en.
const menuTranslations: Record<Language, TranslationStructure> = {
  fr: {
    menu: {
      title: "Notre Menu",
      categories: {
        pizzas: "Pizzas",
        burritos: "Burritos",
        tacos: "Tacos",
        sandwiches: "Sandwiches",
        paninis: "Paninis",
        plats: "Plats",
        salades: "Salades",
        boissons: "Boissons",
        pizza: {
          speciales: "Les Spéciales",
          incontournables: "Les Incontournables",
          fruitsmer: "Les Fruits de Mer",
          mixtes: "Les Mixtes"
        }
      }
    },
    common: {
      menuPDF: "Menu PDF",
      location: "Notre Location",
      call: "Appelez-nous",
      cart: "Panier",
      selectLanguage: "Choisir la langue",
      addToCart: "Ajouter au panier",
      added: "Ajouté!",
      total: "Total",
      checkout: "Commander",
      emptyCart: "Votre panier est vide",
      quantity: "Quantité",
      specialInstructions: "Instructions spéciales",
      addOns: "Suppléments"
    },
    cart: {
      titlePickup: "Commande sur place",
      titleDelivery: "Commande en livraison",
      name: "Nom",
      phone: "Téléphone",
      address: "Adresse",
      submit: "Commander",
      quantity: "Quantité",
      unitPrice: "Prix unitaire",
      subtotal: "Sous-total",
      instructions: "Instructions",
      customerInfo: "Informations client",
      specialInstructions: "Instructions spéciales",
      totalToPay: "Total à payer",
      thankYou: "Merci de votre commande",
      deliveryType: {
        surplace: "Sur Place",
        livraison: "Livraison"
      },
      continue: "Continuer",
      title: "Panier",
      emptyCart: "Votre panier est vide",
      price: "Prix",
      addInstructions: "Ajouter des instructions",
      itemInstructionsPlaceholder: "Instructions spéciales pour cet article...",
      sendToKitchen: "Envoyer à la cuisine",
      proceedToDelivery: "Continuer vers la livraison",
      errorSubmittingOrder: "Une erreur est survenue lors de la commande. Veuillez réessayer.",
      hideInstructions: "Masquer les instructions"
    },
    footer: {
      description: "Restaurant de cuisine fusion offrant une expérience culinaire unique à Ouarzazate",
      contact: "Contact",
      location: "Notre emplacement",
      googleReviews: "Avis Google",
      rating: "4.5/5 basé sur 150+ avis",
      hours: {
        weekdays: "Lun-Ven: 11h00-23h00",
        weekends: "Sam-Dim: 11h00-00h00"
      },
      admin: "Administration"
    },
    review: {
      title: "Aimez-vous notre service ?",
      description: "Votre avis nous aide à nous améliorer et permet à d'autres clients de découvrir notre restaurant.",
      leaveReview: "Laisser un avis sur Google",
      maybeLater: "Plus tard"
    },
    deliveryOption: {
      welcome: "Bienvenue chez DJOCO",
      subtitle: "Comment souhaitez-vous commander?",
      dineIn: {
        title: "Sur place",
        description: "Commander et manger au restaurant"
      },
      delivery: {
        title: "Livraison",
        description: "Se faire livrer à domicile"
      },
      call: {
        title: "Allo DJOCO",
        phone: "+212 6 32 30 28 29"
      }
    },
  },
  ar: {
    menu: {
      title: "قائمتنا",
      categories: {
        pizzas: "بيتزا",
        burritos: "بوريتو",
        tacos: "تاكوس",
        sandwiches: "ساندويتش",
        paninis: "بانيني",
        plats: "أطباق",
        salades: "سلطات",
        boissons: "مشروبات",
        pizza: {
          speciales: "البيتزا الخاصة",
          incontournables: "البيتزا الكلاسيكية",
          fruitsmer: "بيتزا المأكولات البحرية",
          mixtes: "البيتزا المشكلة"
        }
      }
    },
    common: {
      menuPDF: "قائمة PDF",
      location: "موقعنا",
      call: "اتصل بنا",
      cart: "السلة",
      selectLanguage: "اختر اللغة",
      addToCart: "أضف إلى السلة",
      added: "تمت الإضافة!",
      total: "المجموع",
      checkout: "الطلب",
      emptyCart: "السلة فارغة",
      quantity: "الكمية",
      specialInstructions: "تعليمات خاصة",
      addOns: "إضافات"
    },
    cart: {
      titlePickup: "طلب في المطعم",
      titleDelivery: "طلب توصيل",
      name: "الاسم",
      phone: "الهاتف",
      address: "العنوان",
      submit: "تأكيد الطلب",
      quantity: "الكمية",
      unitPrice: "السعر للوحدة",
      subtotal: "المجموع الفرعي",
      instructions: "تعليمات",
      customerInfo: "معلومات العميل",
      specialInstructions: "تعليمات خاصة",
      totalToPay: "المجموع للدفع",
      thankYou: "شكراً لطلبكم",
      deliveryType: {
        surplace: "في المطعم",
        livraison: "توصيل"
      },
      continue: "متابعة",
      title: "السلة",
      emptyCart: "السلة فارغة",
      price: "السعر",
      addInstructions: "إضافة تعليمات",
      itemInstructionsPlaceholder: "تعليمات خاصة لهذا العنصر...",
      sendToKitchen: "إرسال إلى المطبخ",
      proceedToDelivery: "المتابعة إلى التوصيل",
      errorSubmittingOrder: "حدث خطأ أثناء إرسال الطلب. يرجى المحاولة مرة أخرى.",
      hideInstructions: "إخفاء التعليمات"
    },
    footer: {
      description: "مطعم للمأكولات المتنوعة يقدم تجربة طعام فريدة في ورزازات",
      contact: "اتصل بنا",
      location: "موقعنا",
      googleReviews: "تقييمات جوجل",
      rating: "4.5/5 بناءً على +150 تقييم",
      hours: {
        weekdays: "الإثنين-الجمعة: 11:00-23:00",
        weekends: "السبت-الأحد: 11:00-00:00"
      },
      admin: "الإدارة"
    },
    review: {
      title: "هل أعجبتك خدمتنا؟",
      description: "رأيك يساعدنا على التحسن ويساعد العملاء الآخرين في اكتشاف مطعمنا.",
      leaveReview: "اترك تقييماً على جوجل",
      maybeLater: "ربما لاحقاً"
    },
    deliveryOption: {
      welcome: "مرحباً بكم في DJOCO",
      subtitle: "كيف تريد أن تطلب؟",
      dineIn: {
        title: "في المطعم",
        description: "اطلب وتناول طعامك في المطعم"
      },
      delivery: {
        title: "التوصيل",
        description: "اطلب وسنوصّله إلى باب منزلك"
      },
      call: {
        title: "ألو DJOCO",
        phone: "+212 6 32 30 28 29"
      }
    },
  },
  en: {
    menu: {
      title: "Our Menu",
      categories: {
        pizzas: "Pizzas",
        burritos: "Burritos",
        tacos: "Tacos",
        sandwiches: "Sandwiches",
        paninis: "Paninis",
        plats: "Dishes",
        salades: "Salads",
        boissons: "Drinks",
        pizza: {
          speciales: "Special Pizzas",
          incontournables: "Classic Pizzas",
          fruitsmer: "Seafood Pizzas",
          mixtes: "Mixed Pizzas"
        }
      }
    },
    common: {
      menuPDF: "Menu PDF",
      location: "Our Location",
      call: "Call Us",
      cart: "Cart",
      selectLanguage: "Select Language",
      addToCart: "Add to Cart",
      added: "Added!",
      total: "Total",
      checkout: "Checkout",
      emptyCart: "Your cart is empty",
      quantity: "Quantity",
      specialInstructions: "Special Instructions",
      addOns: "Add-ons"
    },
    cart: {
      titlePickup: "Pickup Order",
      titleDelivery: "Delivery Order",
      name: "Name",
      phone: "Phone",
      address: "Address",
      submit: "Place Order",
      quantity: "Quantity",
      unitPrice: "Unit Price",
      subtotal: "Subtotal",
      instructions: "Instructions",
      customerInfo: "Customer Information",
      specialInstructions: "Special Instructions",
      totalToPay: "Total to Pay",
      thankYou: "Thank you for your order",
      deliveryType: {
        surplace: "On Site",
        livraison: "Delivery"
      },
      continue: "Continue",
      title: "Cart",
      emptyCart: "Your cart is empty",
      price: "Price",
      addInstructions: "Add Instructions",
      itemInstructionsPlaceholder: "Special instructions for this item...",
      sendToKitchen: "Send to Kitchen",
      proceedToDelivery: "Proceed to Delivery",
      errorSubmittingOrder: "An error occurred while submitting your order. Please try again.",
      hideInstructions: "Hide Instructions"
    },
    footer: {
      description: "Fusion cuisine restaurant offering a unique dining experience in Ouarzazate",
      contact: "Contact",
      location: "Our Location",
      googleReviews: "Google Reviews",
      rating: "4.5/5 based on 150+ reviews",
      hours: {
        weekdays: "Mon-Fri: 11:00-23:00",
        weekends: "Sat-Sun: 11:00-00:00"
      },
      admin: "Administration"
    },
    review: {
      title: "Enjoying our service?",
      description: "Your review helps us improve and helps other customers discover our restaurant.",
      leaveReview: "Leave a Google Review",
      maybeLater: "Maybe Later"
    },
    deliveryOption: {
      welcome: "Welcome to DJOCO",
      subtitle: "How would you like to order?",
      dineIn: {
        title: "On Site",
        description: "Order and eat at the restaurant"
      },
      delivery: {
        title: "Delivery",
        description: "Order and we deliver to your door"
      },
      call: {
        title: "Call DJOCO",
        phone: "+212 6 32 30 28 29"
      }
    },
  }
};

// Define initializeDefaultData function
const initializeDefaultData = async () => {
  const db = getDatabase();
  
  // Save supplements
  const supplementsRef = ref(db, 'supplements');
  await set(supplementsRef, defaultSupplements);
  
  // Save translations
  await set(ref(db, 'translations'), menuTranslations);
  
  // Initialize categories with translations
  const categoriesRef = ref(db, 'categories');
  const categories: Category[] = Object.entries(menuCategories).map(([id, name], index) => {
    // Get the correct translation for each category
    const frTranslation = menuTranslations.fr.menu.categories[id as keyof typeof menuTranslations.fr.menu.categories];
    const arTranslation = menuTranslations.ar.menu.categories[id as keyof typeof menuTranslations.ar.menu.categories];
    const enTranslation = menuTranslations.en.menu.categories[id as keyof typeof menuTranslations.en.menu.categories];

    // Ensure we're getting string values (unknown-typed: the category label can
    // be a plain string today, but historically was allowed to be an object).
    const getStringValue = (value: unknown): string => {
      if (typeof value === 'string') return value;
      if (value !== null && typeof value === 'object') return String(value);
      return String(value);
    };

    return {
      id,
      name,
      order: index,
      translations: {
        fr: getStringValue(frTranslation),
        ar: getStringValue(arTranslation),
        en: getStringValue(enTranslation)
      }
    };
  });

  await set(categoriesRef, categories);

  // Save menu items (one write per item under menu/<id>)
  for (const item of defaultMenuItems) {
    await set(ref(db, `menu/${item.id}`), item);
  }
};

// Single export statement at the bottom
export { 
  defaultMenuItems, 
  initializeDefaultData, 
  menuTranslations 
};
